import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import {
	ArrowLeftIcon,
	Avatar,
	Button,
	CloseIcon,
	HistoryIcon,
	IconButton,
	Modal,
	Popover,
	tokens,
} from '@treams/treams-ui';

import useTalentReviewPlacement, {
	type TalentReviewPlacementHelpStep,
} from '../hooks/useTalentReviewPlacement';
import { type PlacementHelpCell, averagePlacementHelpCell } from '../talentReviewPlacementHelp';
import SlideSwitcher from './SlideSwitcher';
import TalentReviewPlacementHelpQuestion from './TalentReviewPlacementHelpQuestion';
import TalentReviewPlacementHelpResult, {
	type TalentReviewPlacementPosition,
} from './TalentReviewPlacementHelpResult';

const Body = styled(Modal.Contents)`
	background: ${tokens.surface.secondary};
`;

const HeaderTitle = styled.div`
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.h3}px;
	font-weight: ${tokens.typography.weights.semibold};
	line-height: ${tokens.typography.lineHeights.heading};
`;

const BackSlot = styled(motion.div)`
	overflow: hidden;
	flex-shrink: 0;
	line-height: 0;
`;

const BackSlotInner = styled.div`
	padding-right: ${tokens.gap.list}px;
`;

const AvatarWrap = styled.div`
	position: relative;
	flex-shrink: 0;
	line-height: 0;
`;

const ClockBadge = styled.div`
	position: absolute;
	right: -2px;
	bottom: -2px;
	width: 16px;
	height: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.warning.icon};
	color: ${tokens.text.inverse};
	border: 2px solid ${tokens.surface.white};
	box-sizing: border-box;

	svg {
		display: block;
		width: 10px;
		height: 10px;
		color: inherit;
	}
`;

const ProgressTrack = styled.div`
	display: flex;
	width: 100%;
	gap: ${tokens.gap.inline}px;
`;

const ProgressSegment = styled.button<{ $filled?: boolean }>`
	flex: 1;
	height: 6px;
	padding: 0;
	box-sizing: border-box;
	overflow: hidden;
	border-radius: ${tokens.radius.full}px;
	border: 1px solid ${(props) => (props.$filled ? tokens.brand.icon : tokens.border.subtle)};
	background: ${tokens.surface.white};
	transition: border-color 0.35s;
	cursor: pointer;

	&:disabled {
		cursor: default;
	}

	&:focus-visible {
		outline: 2px solid ${tokens.brand.border};
		outline-offset: 2px;
	}
`;

const ProgressFill = styled(motion.div)`
	height: 100%;
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.brand.icon};
`;

type TalentReviewPlacementHelpModalProps = {
	user: Pick<graphql.User, 'firstName' | 'lastName'> & {
		avatar?: graphql.User['avatar'];
	};
	questions: TalentReviewPlacementHelpStep[];
	current?: TalentReviewPlacementPosition;
	currentCell?: PlacementHelpCell;
	rowCount: number;
	columnCount: number;
	cellPosition: (row: number, column: number) => TalentReviewPlacementPosition;
	onMove?: (row: number, column: number) => void;
	onAnswered?: (answers: graphql.TalentReviewPlacementHelpAnswerInput[]) => void;
	onClose: () => void;
};

export default function TalentReviewPlacementHelpModal(props: TalentReviewPlacementHelpModalProps) {
	const { t } = useTranslation();
	const name = `${props.user.firstName} ${props.user.lastName}`;
	const {
		stepIndex,
		stepCount,
		questions,
		isFirst,
		isResult,
		canGoTo,
		handleGoTo,
		handleSelect,
		handlePrevious,
	} = useTalentReviewPlacement(props.questions);

	const suggestedCell = averagePlacementHelpCell({
		ticks: questions.flatMap((question) => {
			const answer = question.answers.find((option) => option.id === question.selected);
			return answer ? [{ axis: question.axis, tickIndex: answer.tickIndex }] : [];
		}),
		rowCount: props.rowCount,
		columnCount: props.columnCount,
		current: props.currentCell,
	});
	const suggested = props.cellPosition(suggestedCell.row, suggestedCell.column);

	// Report the answers the first time the result step is reached; going back
	// and forth afterwards does not report again.
	const answered = useRef(false);
	useEffect(() => {
		if (!isResult || answered.current) {
			return;
		}
		answered.current = true;
		const answers = questions.flatMap((question) => {
			const answer = question.answers.find((option) => option.id === question.selected);
			return answer ? [{ question: question.question, answer: answer.label }] : [];
		});
		if (!answers.length) {
			return;
		}
		props.onAnswered?.(answers);
	}, [isResult]);

	// Modal.Header only wires Escape when it renders its own (plain) close button.
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				props.onClose();
			}
		};

		document.addEventListener('keydown', onKeyDown);

		return () => document.removeEventListener('keydown', onKeyDown);
	}, []);

	return (
		<Popover visible center onDismiss={props.onClose}>
			<Modal width={520}>
				<Modal.Header
					title={
						<Flex alignItems="center" justifyContent="space-between" style={{ gap: 12 }}>
							<Flex alignItems="center" minWidth={0}>
								<AnimatePresence initial={false}>
									{!isFirst && (
										<BackSlot
											key="back"
											initial={{ width: 0, opacity: 0 }}
											animate={{ width: 'auto', opacity: 1 }}
											exit={{ width: 0, opacity: 0 }}
											transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
										>
											<BackSlotInner>
												<IconButton
													type="secondary"
													icon={<ArrowLeftIcon />}
													aria-label={t('talentReviews.v2.placementHelp.previous')}
													onClick={handlePrevious}
												/>
											</BackSlotInner>
										</BackSlot>
									)}
								</AnimatePresence>
								<Flex alignItems="center" style={{ gap: 12 }} minWidth={0}>
									<AvatarWrap>
										<Avatar size={40} user={props.user} />
										<ClockBadge>
											<HistoryIcon solid />
										</ClockBadge>
									</AvatarWrap>
									<HeaderTitle>{t('talentReviews.v2.placementHelp.title', { name })}</HeaderTitle>
								</Flex>
							</Flex>
							<IconButton
								type="secondary"
								icon={<CloseIcon />}
								aria-label={t('talentReviews.v2.placementHelp.close')}
								onClick={props.onClose}
							/>
						</Flex>
					}
				/>
				<Body>
					<ProgressTrack>
						{Array.from({ length: stepCount }, (_, index) => (
							<ProgressSegment
								key={index}
								type="button"
								$filled={index <= stepIndex}
								disabled={!canGoTo(index)}
								aria-label={t('talentReviews.v2.placementHelp.goToStep', {
									step: index + 1,
								})}
								aria-current={index === stepIndex ? 'step' : undefined}
								onClick={() => handleGoTo(index)}
							>
								<ProgressFill
									initial={false}
									animate={{ width: index <= stepIndex ? '100%' : '0%' }}
									transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
								/>
							</ProgressSegment>
						))}
					</ProgressTrack>
					<SlideSwitcher activeIndex={stepIndex}>
						{questions.map((question, index) => (
							<TalentReviewPlacementHelpQuestion
								key={question.id}
								index={index}
								axisLabel={question.axisLabel}
								question={question.question}
								options={question.answers}
								selected={question.selected}
								onSelect={(answerID) => handleSelect(index, answerID)}
							/>
						))}
						<TalentReviewPlacementHelpResult
							user={props.user}
							current={props.current}
							suggested={suggested}
							onKeep={props.onClose}
							onMove={
								props.onMove
									? () => props.onMove?.(suggestedCell.row, suggestedCell.column)
									: undefined
							}
						/>
					</SlideSwitcher>
				</Body>
				<Modal.Footer>
					<Button
						type="secondary"
						title={t('talentReviews.v2.placementHelp.close')}
						onClick={props.onClose}
					/>
				</Modal.Footer>
			</Modal>
		</Popover>
	);
}
